import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useToast } from '../context/ToastContext';
import api from '../utils/api';
import { useTheme } from '../context/ThemeContext';
import { authAPI } from '../utils/api';

const DEFAULT_SUBJECTS = {
  6: ['Mathematics', 'Science', 'Social Science', 'English', 'Hindi'],
  7: ['Mathematics', 'Science', 'Social Science', 'English', 'Hindi'],
  8: ['Mathematics', 'Science', 'Social Science', 'English', 'Hindi'],
  9: ['Mathematics', 'Science', 'Social Science', 'English', 'Hindi', 'AI / CT'],
  10: ['Mathematics', 'Science', 'Social Science', 'English', 'Hindi', 'AI / CT'],
};

const SignupPage = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [subjectsByClass, setSubjectsByClass] = useState(DEFAULT_SUBJECTS);

  useEffect(() => {
    api.get('/settings/subjects').then(res => {
      if (res.data.data) {
        const converted = {};
        Object.entries(res.data.data).forEach(([k, v]) => { converted[parseInt(k)] = v; });
        setSubjectsByClass(converted);
      }
    }).catch(() => {});
  }, []);

  const [form, setForm] = useState({
    parentName: '', parentEmail: '', parentPhone: '',
    city: '', state: '', pincode: '',
    studentName: '', classId: 9,
    subjects: ['Mathematics', 'Science', 'Social Science', 'English', 'Hindi', 'AI / CT'],
    password: '', confirmPassword: '',
    consentGiven: false
  });

  const { success, error } = useToast();
  const { theme } = useTheme();
  const navigate = useNavigate();

  const validate = (s) => {
    const e = {};
    if (s === 1) {
      if (!form.parentName.trim()) e.parentName = 'Parent name is required';
      if (!form.parentEmail || !/\S+@\S+\.\S+/.test(form.parentEmail)) e.parentEmail = 'Valid email is required';
      if (!form.parentPhone.trim()) e.parentPhone = 'Phone is required';
    }
    if (s === 2) {
      if (!form.studentName.trim()) e.studentName = 'Student name is required';
    }
    if (s === 4) {
      if (!form.password || form.password.length < 8) e.password = 'Password must be at least 8 characters';
      if (form.password !== form.confirmPassword) e.confirmPassword = 'Passwords do not match';
      if (!form.consentGiven) e.consent = 'You must give consent to proceed';
    }
    return e;
  };

  const nextStep = () => {
    const e = validate(step);
    if (Object.keys(e).length) return setErrors(e);
    setErrors({});
    setStep(s => s + 1);
  };

  const toggleSubject = (subj) => {
    setForm(prev => ({
      ...prev,
      subjects: prev.subjects.includes(subj)
        ? prev.subjects.filter(s => s !== subj)
        : [...prev.subjects, subj]
    }));
  };

  const handleSubmit = async () => {
    const e = validate(4);
    if (Object.keys(e).length) return setErrors(e);
    setLoading(true);
    try {
      await authAPI.register({
        parentName: form.parentName,
        parentEmail: form.parentEmail,
        parentPhone: form.parentPhone,
        city: form.city,
        state: form.state,
        pincode: form.pincode,
        studentName: form.studentName,
        classId: form.classId,
        subjects: form.subjects,
        password: form.password,
        consentGiven: 'true'
      });
      setSubmitted(true);
    } catch (err) {
      error(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const StepIndicator = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: '28px' }}>
      {[1, 2, 3, 4].map((s, i) => (
        <div key={s} style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
            <div style={{
              width: '28px', height: '28px', borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '11px', fontWeight: 800,
              background: step > s ? 'var(--green)' : step === s ? 'var(--brand-primary)' : 'var(--bg)',
              color: step >= s ? 'white' : 'var(--muted)',
              border: step < s ? '2px solid var(--border)' : 'none'
            }}>
              {step > s ? '✓' : s}
            </div>
            <span style={{ fontSize: '9px', fontWeight: 700, color: step === s ? 'var(--brand-primary)' : step > s ? 'var(--green)' : 'var(--muted)', whiteSpace: 'nowrap' }}>
              {['Parent Info', 'Child Profile', 'Subjects', 'Consent'][i]}
            </span>
          </div>
          {i < 3 && <div style={{ width: '40px', height: '2px', background: step > s ? 'var(--green)' : 'var(--border)', margin: '0 4px', marginBottom: '16px' }}></div>}
        </div>
      ))}
    </div>
  );

  if (submitted) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', padding: '24px' }}>
        <div style={{ background: 'var(--white)', borderRadius: 'var(--radius)', padding: '40px', maxWidth: '440px', width: '100%', textAlign: 'center' }}>
          <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'var(--brand-primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px', margin: '0 auto 16px' }}>🕐</div>
          <h2 style={{ fontSize: '20px', fontWeight: 800, marginBottom: '10px' }}>Registration submitted!</h2>
          <p style={{ fontSize: '13px', color: 'var(--muted)', lineHeight: 1.6, marginBottom: '16px' }}>
            Your account is under review. Our team will verify and approve within 24 hours. You will receive an email at <strong>{form.parentEmail}</strong> once approved.
          </p>
          <div style={{ background: 'var(--yellow-light)', color: '#7C2D12', fontSize: '12px', fontWeight: 800, borderRadius: '20px', padding: '6px 16px', display: 'inline-block', marginBottom: '24px' }}>⏳ Pending Approval</div>
          <br />
          <button onClick={() => navigate('/')} className="btn-primary">Back to Login</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', background: 'var(--bg)' }}>

      {/* Left panel */}
      <div style={{ width: '380px', minWidth: '380px', background: 'var(--brand-secondary)', padding: '48px 40px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ width: '38px', height: '38px', borderRadius: '9px', background: 'var(--brand-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Baloo 2', cursive", fontSize: '17px', fontWeight: 800, color: 'white' }}>G</div>
          <div style={{ fontFamily: "'Baloo 2', cursive", fontSize: '15px', fontWeight: 800, color: 'var(--brand-primary)' }}>MyPersonalGuruji</div>
        </div>
        <div style={{ fontFamily: "'Baloo 2', cursive", fontSize: '26px', fontWeight: 800, color: 'white', lineHeight: 1.2 }}>
          Give your child a <span style={{ color: 'var(--brand-primary)' }}>Guruji</span> who never gives up on them.
        </div>
        <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', lineHeight: 1.7 }}>
          Complete CBSE learning platform for Class 6–10. Study, revise and master every topic at your child's own pace.
        </div>
        <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 'var(--radius-sm)', padding: '14px', cursor: 'pointer' }} onClick={() => navigate('/signup/school')}>
          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>
            🏫 Are you a school? <span style={{ color: 'var(--brand-primary-mid)', fontWeight: 800 }}>Register your school →</span>
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
        <div style={{ width: '100%', maxWidth: '480px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 800 }}>Create Account</h2>
            <span style={{ fontSize: '12px', color: 'var(--muted)' }}>Have an account? <Link to="/" style={{ color: 'var(--brand-primary)', fontWeight: 800 }}>Log in</Link></span>
          </div>
          <StepIndicator />

          {/* STEP 1 — Parent Info */}
          {step === 1 && (
            <div className="fade-up">
              <h3 style={{ fontSize: '15px', fontWeight: 800, marginBottom: '4px' }}>Step 1 — Parent Information</h3>
              <p style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '20px' }}>As per DPDP Act 2023, parent details are required for minor accounts.</p>

              <div className="form-group">
                <label className="form-label">Parent / Guardian name *</label>
                <input className={`form-input ${errors.parentName ? 'error' : ''}`} type="text" placeholder="Your full name" value={form.parentName} onChange={e => setForm(p => ({ ...p, parentName: e.target.value }))} />
                {errors.parentName && <span className="form-error">{errors.parentName}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">Parent email *</label>
                <input className={`form-input ${errors.parentEmail ? 'error' : ''}`} type="email" placeholder="your@email.com" value={form.parentEmail} onChange={e => setForm(p => ({ ...p, parentEmail: e.target.value }))} />
                {errors.parentEmail && <span className="form-error">{errors.parentEmail}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">Parent phone *</label>
                <input className={`form-input ${errors.parentPhone ? 'error' : ''}`} type="tel" placeholder="10-digit mobile number" value={form.parentPhone} onChange={e => setForm(p => ({ ...p, parentPhone: e.target.value }))} />
                {errors.parentPhone && <span className="form-error">{errors.parentPhone}</span>}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                <div className="form-group">
                  <label className="form-label">City</label>
                  <input className="form-input" type="text" placeholder="Mumbai" value={form.city} onChange={e => setForm(p => ({ ...p, city: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">State</label>
                  <input className="form-input" type="text" placeholder="Maharashtra" value={form.state} onChange={e => setForm(p => ({ ...p, state: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Pincode</label>
                  <input className="form-input" type="text" placeholder="400001" value={form.pincode} onChange={e => setForm(p => ({ ...p, pincode: e.target.value }))} />
                </div>
              </div>

              <button onClick={nextStep} className="btn-primary btn-full" style={{ marginTop: '8px' }}>Continue →</button>
            </div>
          )}

          {/* STEP 2 — Child Profile */}
          {step === 2 && (
            <div className="fade-up">
              <h3 style={{ fontSize: '15px', fontWeight: 800, marginBottom: '4px' }}>Step 2 — Child Profile</h3>
              <p style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '20px' }}>Tell us about your child.</p>

              <div className="form-group">
                <label className="form-label">Student name *</label>
                <input className={`form-input ${errors.studentName ? 'error' : ''}`} type="text" placeholder="Child's full name" value={form.studentName} onChange={e => setForm(p => ({ ...p, studentName: e.target.value }))} />
                {errors.studentName && <span className="form-error">{errors.studentName}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">Class *</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' }}>
                  {[6, 7, 8, 9, 10].map(c => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setForm(p => ({ ...p, classId: c, subjects: subjectsByClass[c].slice(0, 6) }))}
                      style={{
                        padding: '9px 4px', borderRadius: 'var(--radius-sm)',
                        border: form.classId === c ? '1.5px solid var(--brand-primary)' : '1.5px solid var(--border)',
                        background: form.classId === c ? 'var(--brand-primary-light)' : 'var(--bg)',
                        color: form.classId === c ? 'var(--brand-primary)' : 'var(--muted)',
                        fontSize: '13px', fontWeight: 800, cursor: 'pointer'
                      }}
                    >
                      {c}th
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                <button onClick={() => setStep(1)} className="btn-secondary" style={{ flex: 1 }}>← Back</button>
                <button onClick={nextStep} className="btn-primary" style={{ flex: 2 }}>Continue →</button>
              </div>
            </div>
          )}

          {/* STEP 3 — Subjects */}
          {step === 3 && (
            <div className="fade-up">
              <h3 style={{ fontSize: '15px', fontWeight: 800, marginBottom: '4px' }}>Step 3 — Select Subjects</h3>
              <p style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '20px' }}>All subjects selected by default. Deselect any not needed. You can change later.</p>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '7px', marginBottom: '20px' }}>
                {(subjectsByClass[form.classId] || []).map(subj => {
                  const checked = form.subjects.includes(subj);
                  return (
                    <div
                      key={subj}
                      onClick={() => toggleSubject(subj)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '9px 11px', borderRadius: 'var(--radius-sm)',
                        border: checked ? '1.5px solid var(--green)' : '1.5px solid var(--border)',
                        background: checked ? 'var(--green-light)' : 'var(--bg)',
                        cursor: 'pointer'
                      }}
                    >
                      <div style={{ width: '16px', height: '16px', borderRadius: '4px', border: checked ? 'none' : '1.5px solid var(--border)', background: checked ? 'var(--green)' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '10px', color: 'white', fontWeight: 800 }}>
                        {checked ? '✓' : ''}
                      </div>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: checked ? 'var(--green)' : 'var(--text)' }}>{subj}</span>
                    </div>
                  );
                })}
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => setStep(2)} className="btn-secondary" style={{ flex: 1 }}>← Back</button>
                <button onClick={nextStep} className="btn-primary" style={{ flex: 2 }}>Continue →</button>
              </div>
            </div>
          )}

          {/* STEP 4 — Password + Consent */}
          {step === 4 && (
            <div className="fade-up">
              <h3 style={{ fontSize: '15px', fontWeight: 800, marginBottom: '4px' }}>Step 4 — Password & Consent</h3>
              <p style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '20px' }}>Create a password and give your consent to complete registration.</p>

              <div className="form-group">
                <label className="form-label">Create password *</label>
                <input className={`form-input ${errors.password ? 'error' : ''}`} type="password" placeholder="Min. 8 characters" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} />
                {errors.password && <span className="form-error">{errors.password}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">Confirm password *</label>
                <input className={`form-input ${errors.confirmPassword ? 'error' : ''}`} type="password" placeholder="Repeat password" value={form.confirmPassword} onChange={e => setForm(p => ({ ...p, confirmPassword: e.target.value }))} />
                {errors.confirmPassword && <span className="form-error">{errors.confirmPassword}</span>}
              </div>

              <div style={{ background: 'var(--bg)', border: errors.consent ? '1.5px solid var(--red)' : '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '12px 14px', display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '4px' }}>
                <input
                  type="checkbox"
                  id="consent"
                  checked={form.consentGiven}
                  onChange={e => setForm(p => ({ ...p, consentGiven: e.target.checked }))}
                  style={{ marginTop: '2px', flexShrink: 0, width: '16px', height: '16px', cursor: 'pointer' }}
                />
                <label htmlFor="consent" style={{ fontSize: '11px', color: 'var(--muted)', lineHeight: 1.5, cursor: 'pointer' }}>
                  I confirm I am the parent or legal guardian of this child. I consent to MyPersonalGuruji collecting and storing my child's learning data as described in the{' '}
                  <span style={{ color: 'var(--brand-primary)', fontWeight: 700 }}>Privacy Policy</span>.
                  I understand this data will not be sold to third parties.{' '}
                  <span style={{ color: 'var(--brand-primary)', fontWeight: 700 }}>DPDP Act 2023 compliant.</span>
                </label>
              </div>
              {errors.consent && <span className="form-error">{errors.consent}</span>}

              <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
                <button onClick={() => setStep(3)} className="btn-secondary" style={{ flex: 1 }}>← Back</button>
                <button onClick={handleSubmit} className="btn-primary" style={{ flex: 2 }} disabled={loading}>
                  {loading ? 'Submitting...' : '✓ Submit Registration'}
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default SignupPage;
